import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarCheck,
  CalendarDays,
  Clock3,
  Save,
  ShieldCheck,
  Store,
  UserRound,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../api/api";
import FilaZeroSelect from "../../../components/FilaZeroSelect";
import ClientesBloqueadosSelector from "./ClientesBloqueadosSelector";
import { getProfissionalLabels } from "../../../utils/profissionalRotulo";
import { montarOpcoesRotuloProfissional } from "../../../utils/rotulosPorTipoEstabelecimento";
import "./componentes.css";

const CONFIRMACAO_OPTIONS = [
  { value: 10, label: "10 minutos" },
  { value: 20, label: "20 minutos" },
  { value: 30, label: "30 minutos" },
  { value: 45, label: "45 minutos" },
  { value: 60, label: "1 hora" },
];

const FINALIZACAO_OPTIONS = [
  { value: 30, label: "30 minutos" },
  { value: 60, label: "1 hora" },
  { value: 120, label: "2 horas" },
  { value: 180, label: "3 horas" },
  { value: 240, label: "4 horas" },
];

const INTERVALO_OPTIONS = [
  { value: 0, label: "Sem intervalo" },
  { value: 5, label: "5 minutos" },
  { value: 10, label: "10 minutos" },
  { value: 15, label: "15 minutos" },
  { value: 20, label: "20 minutos" },
  { value: 30, label: "30 minutos" },
  { value: 45, label: "45 minutos" },
  { value: 60, label: "1 hora" },
];

const PROTECAO_APP_REGRAS = [
  {
    icon: CalendarCheck,
    titulo: "1 reserva por vez",
    descricao: "Apenas um agendamento em aberto neste estabelecimento.",
  },
  {
    icon: CalendarDays,
    titulo: "Até 5 por semana",
    descricao: "Limite de cinco agendamentos a cada 7 dias, por estabelecimento.",
  },
  {
    icon: Clock3,
    titulo: "Sem sobreposição",
    descricao: "Não reserva em horários que conflitam com outro atendimento do cliente.",
  },
  {
    icon: Store,
    titulo: "Sem choque de horário",
    descricao: "Não reserva em horários já ocupados em outro estabelecimento.",
  },
  {
    icon: ShieldCheck,
    titulo: "Janela de 30 dias",
    descricao: "No app, o cliente só vê datas dentro dos próximos 30 dias.",
  },
];

const ConfiguracaoAgendamento = ({
  config,
  onSalvar,
  salvando = false,
  estabelecimentoId,
}) => {
  const [dados, setDados] = useState({
    confirmacao_automatica_ativo: config.confirmacao_automatica_ativo,
    confirmacao_automatica_minutos: config.confirmacao_automatica_minutos,
    finalizacao_automatica_ativo: config.finalizacao_automatica_ativo,
    finalizacao_automatica_minutos: config.finalizacao_automatica_minutos,
    cancelamento_automatico_ativo: config.cancelamento_automatico_ativo,
    cancelamento_automatico_minutos: config.cancelamento_automatico_minutos,
    bloqueio_cliente_muitos_cancelamentos:
      config.bloqueio_cliente_muitos_cancelamentos,
    limite_cancelamentos_cliente: config.limite_cancelamentos_cliente,
    intervalo_minimo_atendimento: config.intervalo_minimo_atendimento || 0,
    rotulo_profissional: config.rotulo_profissional || "",
  });
  const [clientesBloqueados, setClientesBloqueados] = useState(
    Array.isArray(config.clientes_bloqueados) ? config.clientes_bloqueados : []
  );
  const [tipoEstabelecimentoNome, setTipoEstabelecimentoNome] = useState("");
  const [rotulosDoTipo, setRotulosDoTipo] = useState([]);

  useEffect(() => {
    setDados({
      confirmacao_automatica_ativo: config.confirmacao_automatica_ativo,
      confirmacao_automatica_minutos: config.confirmacao_automatica_minutos,
      finalizacao_automatica_ativo: config.finalizacao_automatica_ativo,
      finalizacao_automatica_minutos: config.finalizacao_automatica_minutos,
      cancelamento_automatico_ativo: config.cancelamento_automatico_ativo,
      cancelamento_automatico_minutos: config.cancelamento_automatico_minutos,
      bloqueio_cliente_muitos_cancelamentos:
        config.bloqueio_cliente_muitos_cancelamentos,
      limite_cancelamentos_cliente: config.limite_cancelamentos_cliente,
      intervalo_minimo_atendimento: config.intervalo_minimo_atendimento || 0,
      rotulo_profissional: config.rotulo_profissional || "",
    });
    setClientesBloqueados(
      Array.isArray(config.clientes_bloqueados) ? config.clientes_bloqueados : []
    );
  }, [config]);

  useEffect(() => {
    if (!estabelecimentoId) {
      setTipoEstabelecimentoNome("");
      setRotulosDoTipo([]);
      return undefined;
    }

    let cancelado = false;
    (async () => {
      try {
        const [{ data: estabelecimento }, tiposRes] = await Promise.all([
          api.get(`/estabelecimentos/${estabelecimentoId}`),
          api.get(`/tipos-estabelecimento`),
        ]);
        if (cancelado) return;
        const tipos = Array.isArray(tiposRes.data)
          ? tiposRes.data
          : tiposRes.data?.resultados || [];
        const tipo = tipos.find(
          (item) => String(item.id) === String(estabelecimento?.tipo_id)
        );
        setTipoEstabelecimentoNome(tipo?.nome || "");
        setRotulosDoTipo(tipo?.rotulos_profissional || []);
      } catch (error) {
        console.warn("[ConfiguracaoAgendamento] falha ao carregar tipo:", error);
        if (!cancelado) {
          setTipoEstabelecimentoNome("");
          setRotulosDoTipo([]);
        }
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [estabelecimentoId]);

  const opcoesRotulo = useMemo(
    () =>
      montarOpcoesRotuloProfissional({
        rotulosDoTipo,
        valorAtual: dados.rotulo_profissional,
      }),
    [rotulosDoTipo, dados.rotulo_profissional]
  );

  const previewRotulo = getProfissionalLabels({
    rotulo_profissional: dados.rotulo_profissional,
  });

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (
      type === "checkbox" &&
      name === "cancelamento_automatico_ativo" &&
      checked &&
      (dados.confirmacao_automatica_ativo ||
        dados.finalizacao_automatica_ativo)
    ) {
      toast.warning(
        "Desative a confirmação e a finalização automáticas antes de habilitar o cancelamento automático."
      );
      return;
    }

    const nextValue =
      type === "checkbox"
        ? checked
        : type === "number"
          ? value === ""
            ? ""
            : Number(value)
          : value;

    setDados((current) => {
      const next = { ...current, [name]: nextValue };

      if (
        type === "checkbox" &&
        checked &&
        (name === "confirmacao_automatica_ativo" ||
          name === "finalizacao_automatica_ativo")
      ) {
        next.cancelamento_automatico_ativo = false;
      }

      return next;
    });
  };

  const handleSalvar = async () => {
    if (
      dados.cancelamento_automatico_ativo &&
      (dados.confirmacao_automatica_ativo ||
        dados.finalizacao_automatica_ativo)
    ) {
      toast.warning(
        "O cancelamento automático não pode funcionar junto com a confirmação ou a finalização automática."
      );
      return;
    }

    await onSalvar({
      ...dados,
      rotulo_profissional: dados.rotulo_profissional?.trim() || null,
      ...(dados.bloqueio_cliente_muitos_cancelamentos
        ? {
            clientes_bloqueados_ids: clientesBloqueados.map(
              (cliente) => cliente.id
            ),
          }
        : {}),
    });
  };

  return (
    <div className="secao-config">
      <h2>Configurações de agendamento</h2>

      <div className="config-secao config-secao--rotulo-app">
        <h3>
          <UserRound size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />
          Como o cliente vê no app
        </h3>
          <p className="config-descricao-pequena">
          Escolha como o cliente vê no app e no painel, conforme o tipo do
          estabelecimento
          {tipoEstabelecimentoNome ? ` (${tipoEstabelecimentoNome})` : ""}.
          Sem seleção, o sistema usa &quot;Profissional&quot;.
        </p>

        <div className="config-item">
          <label htmlFor="rotulo_profissional">Como chamar no app e no painel</label>
          <FilaZeroSelect
            name="rotulo_profissional"
            value={dados.rotulo_profissional || ""}
            options={opcoesRotulo}
            onChange={handleChange}
            isSearchable={false}
            placeholder="Usar padrão do sistema"
            ariaLabel="Rótulo do profissional"
            isDisabled={opcoesRotulo.length <= 1}
          />
          <p className="config-nota">
            {opcoesRotulo.length <= 1
              ? "Este tipo ainda não tem rótulos específicos — permanece o padrão Profissional."
              : `Prévia: "${previewRotulo.novo} ${previewRotulo.singular}" · menu "${previewRotulo.plural}"`}
          </p>
        </div>
      </div>

      <div className="config-secao">
        <h3>Confirmação automática</h3>
        <p className="config-descricao-pequena">
          Confirme agendamentos pendentes automaticamente após o período
          definido.
        </p>

        <div className="config-item">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="confirmacao_automatica_ativo"
              checked={dados.confirmacao_automatica_ativo}
              onChange={handleChange}
            />
            <span>Habilitar confirmação automática</span>
          </label>
          <p className="config-nota">
            {dados.confirmacao_automatica_ativo
              ? "No app, hoje é permitido; horários respeitam o tempo de confirmação + 5 min de folga."
              : "No app, reservas só a partir de amanhã."}
          </p>
        </div>

        {dados.confirmacao_automatica_ativo && (
          <div className="config-item">
            <label>Confirmar após</label>
            <FilaZeroSelect
              name="confirmacao_automatica_minutos"
              value={dados.confirmacao_automatica_minutos}
              options={CONFIRMACAO_OPTIONS}
              onChange={handleChange}
              isSearchable={false}
              ariaLabel="Tempo para confirmação automática"
            />
            <p className="config-nota">
              O cliente receberá uma notificação quando a confirmação ocorrer.
            </p>
          </div>
        )}
      </div>

      <div className="config-secao">
        <h3>Finalização automática</h3>
        <p className="config-descricao-pequena">
          Finalize atendimentos confirmados após o horário previsto do serviço.
        </p>

        <div className="config-item">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="finalizacao_automatica_ativo"
              checked={dados.finalizacao_automatica_ativo}
              onChange={handleChange}
            />
            <span>Habilitar finalização automática</span>
          </label>
        </div>

        {dados.finalizacao_automatica_ativo && (
          <div className="config-item">
            <label>Finalizar após</label>
            <FilaZeroSelect
              name="finalizacao_automatica_minutos"
              value={dados.finalizacao_automatica_minutos}
              options={FINALIZACAO_OPTIONS}
              onChange={handleChange}
              isSearchable={false}
              ariaLabel="Tempo para finalização automática"
            />
            <p className="config-nota">
              A regra é aplicada somente a agendamentos confirmados.
            </p>
          </div>
        )}
      </div>

      <div className="config-secao">
        <h3>Cancelamento automático</h3>
        <p className="config-descricao-pequena">
          Cancele agendamentos que permanecerem pendentes além do limite.
        </p>
        <p className="config-alerta">
          Desative a confirmação e a finalização automáticas para usar esta
          opção.
        </p>

        <div className="config-item">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="cancelamento_automatico_ativo"
              checked={dados.cancelamento_automatico_ativo}
              onChange={handleChange}
              disabled={
                dados.confirmacao_automatica_ativo ||
                dados.finalizacao_automatica_ativo
              }
            />
            <span>Habilitar cancelamento automático</span>
          </label>
        </div>

        {dados.cancelamento_automatico_ativo && (
          <div className="config-item">
            <label>Cancelar após (minutos)</label>
            <input
              type="number"
              name="cancelamento_automatico_minutos"
              value={dados.cancelamento_automatico_minutos}
              onChange={handleChange}
              min="30"
              step="30"
            />
            <p className="config-nota">O período mínimo é de 30 minutos.</p>
          </div>
        )}
      </div>

      <div className="config-secao">
        <h3>Grade de disponibilidade</h3>

        <div className="config-item">
          <label>Intervalo entre horários oferecidos</label>
          <FilaZeroSelect
            name="intervalo_minimo_atendimento"
            value={dados.intervalo_minimo_atendimento}
            options={INTERVALO_OPTIONS}
            onChange={handleChange}
            isSearchable={false}
            ariaLabel="Intervalo entre horários oferecidos"
          />
          <p className="config-nota">
            Define a grade de horários na aba Disponibilidade. Com 30 minutos,
            após cadastrar 09:00 o próximo horário válido é 09:30 — não 09:15
            ou 09:20. A contagem começa na abertura de cada dia.
          </p>
        </div>
      </div>

      <div className="config-secao config-secao--protecao">
        <div className="protecao-app__header">
          <div className="protecao-app__badge" aria-hidden="true">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h3>Proteção automática no app</h3>
            <p className="config-descricao-pequena protecao-app__intro">
              Regras fixas para clientes no aplicativo. Não exigem configuração
              manual e ajudam a evitar reservas em excesso.
            </p>
          </div>
        </div>

        <div className="protecao-app__grid">
          {PROTECAO_APP_REGRAS.map(({ icon: Icone, titulo, descricao }) => (
            <div key={titulo} className="protecao-app__card">
              <span className="protecao-app__card-icon" aria-hidden="true">
                <Icone size={17} />
              </span>
              <div className="protecao-app__card-copy">
                <strong>{titulo}</strong>
                <span>{descricao}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="config-secao">
        <h3>Controle de cancelamentos</h3>

        <div className="config-item">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="bloqueio_cliente_muitos_cancelamentos"
              checked={dados.bloqueio_cliente_muitos_cancelamentos}
              onChange={handleChange}
            />
            <span>Bloquear cliente após cancelamentos recorrentes</span>
          </label>
          {!dados.bloqueio_cliente_muitos_cancelamentos && (
            <p className="config-nota">
              Enquanto desativado, nenhum bloqueio por cancelamento é aplicado no
              app.
            </p>
          )}
        </div>

        {dados.bloqueio_cliente_muitos_cancelamentos && (
          <>
            <div className="config-item">
              <label>Quantidade de cancelamentos antes do bloqueio</label>
              <input
                type="number"
                name="limite_cancelamentos_cliente"
                value={dados.limite_cancelamentos_cliente}
                onChange={handleChange}
                min="1"
              />
              <p className="config-nota">
                Ao atingir esse número, o cliente é incluído automaticamente na
                lista abaixo e deixa de agendar pelo app. Só o admin pode
                removê-lo.
              </p>
            </div>

            <div className="config-item">
              <label>Clientes bloqueados</label>
              <ClientesBloqueadosSelector
                estabelecimentoId={estabelecimentoId}
                value={clientesBloqueados}
                onChange={setClientesBloqueados}
                disabled={salvando}
              />
              <p className="config-nota">
                Adicione manualmente ou aguarde o bloqueio automático após os
                cancelamentos. A regra só vale com a opção acima ativa.
              </p>
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        className="btn-salvar"
        onClick={handleSalvar}
        disabled={salvando}
      >
        <Save size={18} aria-hidden="true" />
        {salvando ? "Salvando..." : "Salvar configurações"}
      </button>
    </div>
  );
};

export default ConfiguracaoAgendamento;
